import { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  ChevronUp,
  Download,
  Eye,
  File,
  Folder,
  RefreshCw,
  ShieldCheck,
  X,
} from 'lucide-react';
import { getResponseErrorMessage } from '../../../utils/apiError';
import { formatBytes } from '../../../utils/customUi';

interface RuntimeRootResponse {
  contract: 'custom-instance-runtime-files-v1';
  schemaVersion: 1;
  instanceId: string;
  specHash: string;
  runtimeFilesRoot: string;
  instanceState: string;
  containerState: 'running' | 'stopped' | null;
  available: boolean;
  availability:
    | 'available'
    | 'prepared'
    | 'missing'
    | 'deleting'
    | 'deleted'
    | 'root_not_configured'
    | 'persistent_mount_overlap';
  limits: {
    listTimeoutMs: number;
    listMaxArchiveBytes: number;
    listMaxEntries: number;
    listMaxDepth: number;
    previewMaxBytes: number;
  };
}

interface RuntimeFileEntry {
  name: string;
  path: string;
  type: 'directory' | 'file';
  size: number | null;
  modified: string | null;
  mode: number;
}

interface RuntimeListResponse {
  path: string;
  parentPath: string | null;
  entries: RuntimeFileEntry[];
  scannedEntries: number;
  scannedBytes: number;
  truncatedBy?: 'timeout' | 'archive_bytes' | 'entries' | 'depth';
}

interface RuntimeReadResponse {
  path: string;
  name: string;
  size: number;
  modified: string | null;
  encoding: 'utf8' | 'base64';
  content: string;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function availabilityMessage(root: RuntimeRootResponse): string {
  if (root.availability === 'prepared') return 'The container has not been created yet. Start the instance once to materialize its image-backed filesystem.';
  if (root.availability === 'missing') return 'The exact custom container is missing on the Agent.';
  if (root.availability === 'deleting') return 'The instance is deleting and its runtime filesystem is unavailable.';
  if (root.availability === 'deleted') return 'The instance was deleted and no runtime container remains.';
  if (root.availability === 'root_not_configured') return 'The pinned launch spec does not declare a valid runtime files root.';
  if (root.availability === 'persistent_mount_overlap') return 'The pinned root belongs to a persistent mount. Use Persistent volumes instead.';
  return 'Runtime files are unavailable.';
}

function mode(value: number): string {
  return `0${(value & 0o777).toString(8)}`;
}

export function CustomRuntimeFiles({ instanceId }: { instanceId: string }) {
  const [root, setRoot] = useState<RuntimeRootResponse | null>(null);
  const [currentPath, setCurrentPath] = useState('/');
  const [parentPath, setParentPath] = useState<string | null>(null);
  const [entries, setEntries] = useState<RuntimeFileEntry[]>([]);
  const [scan, setScan] = useState<{
    entries: number;
    bytes: number;
    truncatedBy?: RuntimeListResponse['truncatedBy'];
  } | null>(null);
  const [preview, setPreview] = useState<RuntimeReadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [actionError, setActionError] = useState('');

  const fetchRoot = useCallback(async () => {
    const response = await fetch(`/api/admin/custom-instances/${instanceId}/files/runtime`, {
      headers: authHeaders(),
    });
    if (!response.ok) throw new Error(await getResponseErrorMessage(response, 'Failed to load runtime files'));
    const data = await response.json() as RuntimeRootResponse;
    setRoot(data);
    return data;
  }, [instanceId]);

  const fetchFiles = useCallback(async (path: string) => {
    const response = await fetch(
      `/api/admin/custom-instances/${instanceId}/files/runtime/list?path=${encodeURIComponent(path)}`,
      { headers: authHeaders() },
    );
    if (!response.ok) throw new Error(await getResponseErrorMessage(response, 'Failed to list runtime files'));
    const data = await response.json() as RuntimeListResponse;
    setEntries(Array.isArray(data.entries) ? data.entries : []);
    setCurrentPath(data.path);
    setParentPath(data.parentPath);
    setScan({
      entries: data.scannedEntries,
      bytes: data.scannedBytes,
      ...(data.truncatedBy ? { truncatedBy: data.truncatedBy } : {}),
    });
  }, [instanceId]);

  const refresh = useCallback(async (path = currentPath) => {
    setLoading(true);
    setListError('');
    setActionError('');
    try {
      const data = await fetchRoot();
      if (data.available) await fetchFiles(path);
      else {
        setEntries([]);
        setScan(null);
        setCurrentPath('/');
        setParentPath(null);
      }
    } catch (caught) {
      // Keep the last successful listing visible. A failed refresh must not turn
      // it into a false empty directory, nor should an action failure hide it.
      setListError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  }, [currentPath, fetchFiles, fetchRoot]);

  useEffect(() => {
    setRoot(null);
    setCurrentPath('/');
    setParentPath(null);
    setEntries([]);
    setScan(null);
    setPreview(null);
    setListError('');
    setActionError('');
    void refresh('/');
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPreview = async (entry: RuntimeFileEntry) => {
    setActionError('');
    try {
      const response = await fetch(
        `/api/admin/custom-instances/${instanceId}/files/runtime/read?path=${encodeURIComponent(entry.path)}`,
        { headers: authHeaders() },
      );
      if (!response.ok) {
        setActionError(await getResponseErrorMessage(response, 'Runtime file preview failed'));
        return;
      }
      setPreview(await response.json() as RuntimeReadResponse);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const download = async (entry: RuntimeFileEntry) => {
    setActionError('');
    try {
      const response = await fetch(
        `/api/admin/custom-instances/${instanceId}/files/runtime/download?path=${encodeURIComponent(entry.path)}`,
        { headers: authHeaders() },
      );
      if (!response.ok) {
        setActionError(await getResponseErrorMessage(response, 'Runtime file download failed'));
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = entry.name;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  return <section className="space-y-4" aria-labelledby="custom-runtime-files-title">
    <div>
      <h2 id="custom-runtime-files-title" className="section-title">Runtime files (read-only, image-backed)</h2>
      <p className="section-subtitle mt-1">This is the ephemeral container filesystem. Publish a new image build for permanent changes; persistent mounts remain in the separate manager above.</p>
    </div>

    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-dark-700 flex flex-wrap items-center gap-3 text-xs">
        <ShieldCheck className="w-4 h-4 text-neon-400" />
        <span className="badge-online">read-only</span>
        <div><span className="text-gray-600">Pinned root</span><code className="ml-2 text-cyber-400">{root?.runtimeFilesRoot || 'unavailable'}</code></div>
        <div><span className="text-gray-600">Container</span><span className="ml-2 text-gray-300">{root?.containerState || root?.instanceState || 'unknown'}</span></div>
        <button type="button" className="btn-ghost p-2 ml-auto" title="Refresh runtime files" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {listError && <div className="m-4 p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-center gap-2 text-red-300 text-sm">
        <AlertCircle className="w-4 h-4 shrink-0" /><span>{listError}</span><button type="button" className="ml-auto" aria-label="Dismiss runtime files listing error" onClick={() => setListError('')}><X className="w-4 h-4" /></button>
      </div>}

      {actionError && <div className="m-4 p-3 bg-red-900/30 border border-red-800 rounded-lg flex items-center gap-2 text-red-300 text-sm">
        <AlertCircle className="w-4 h-4 shrink-0" /><span>{actionError}</span><button type="button" className="ml-auto" aria-label="Dismiss runtime file action error" onClick={() => setActionError('')}><X className="w-4 h-4" /></button>
      </div>}

      {scan?.truncatedBy && <div className="m-4 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg flex items-start gap-2 text-amber-200 text-sm">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>Partial listing: the Docker archive scan reached its {scan.truncatedBy.replace('_', ' ')} safety limit. The visible entries are safe to browse; opening a directory scans only that subtree.</span>
      </div>}

      {!loading && root && !root.available && <div className="empty-state m-4"><Folder className="empty-state-icon" /><div className="empty-state-title">Runtime files unavailable</div><p className="empty-state-text">{availabilityMessage(root)}</p></div>}

      {root?.available && <>
        <div className="px-4 py-2 border-b border-dark-700 flex flex-wrap items-center gap-2">
          <button type="button" className="btn-ghost p-2" title="Parent directory" disabled={!parentPath || loading} onClick={() => parentPath && void refresh(parentPath)}><ChevronUp className="w-4 h-4" /></button>
          <code className="text-xs text-gray-400">{currentPath}</code>
          {scan && <span className="ml-auto text-xs text-gray-600">Scanned {scan.entries} entries / {formatBytes(scan.bytes)}</span>}
        </div>

        <div className="overflow-x-auto min-h-40">
          {loading ? <div className="py-12"><div className="spinner w-8 h-8 mx-auto" /></div> : scan === null ? null : entries.length === 0
            ? scan?.truncatedBy
              ? <div className="py-12 text-center text-sm text-amber-200">No entries were discovered before the bounded scan stopped. This directory was not confirmed empty.</div>
              : <div className="py-12 text-center text-sm text-gray-500">This runtime directory is empty.</div>
            : <table className="w-full text-sm"><thead className="text-left text-gray-500 bg-dark-900/20"><tr><th className="px-4 py-2">Name</th><th className="px-4 py-2">Size</th><th className="px-4 py-2">Mode</th><th className="px-4 py-2">Modified</th><th className="px-4 py-2 text-right">Read-only actions</th></tr></thead><tbody className="divide-y divide-dark-700">{entries.map(entry => <tr key={entry.path}>
              <td className="px-4 py-3"><button type="button" className="flex items-center gap-2 text-gray-200 hover:text-neon-300" onClick={() => entry.type === 'directory' ? void refresh(entry.path) : void openPreview(entry)}>{entry.type === 'directory' ? <Folder className="w-4 h-4 text-yellow-500" /> : <File className="w-4 h-4 text-gray-500" />}{entry.name}</button></td>
              <td className="px-4 py-3 text-gray-500">{entry.size === null ? '—' : formatBytes(entry.size)}</td>
              <td className="px-4 py-3 text-gray-500"><code>{mode(entry.mode)}</code></td>
              <td className="px-4 py-3 text-gray-500">{entry.modified ? new Date(entry.modified).toLocaleString() : '—'}</td>
              <td className="px-4 py-3"><div className="flex justify-end gap-1">{entry.type === 'file' && <><button type="button" className="btn-ghost p-2" title="Preview" onClick={() => void openPreview(entry)}><Eye className="w-4 h-4" /></button><button type="button" className="btn-ghost p-2" title="Download" onClick={() => void download(entry)}><Download className="w-4 h-4" /></button></>}</div></td>
            </tr>)}</tbody></table>}
        </div>
      </>}
    </div>

    {preview && <div className="fixed inset-0 bg-black/75 z-50 p-4 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="runtime-file-preview-title">
      <div className="card w-full max-w-5xl h-[75vh] flex flex-col">
        <div className="p-4 border-b border-dark-700 flex flex-wrap items-center gap-3"><Eye className="w-5 h-5 text-neon-400" /><div><h3 id="runtime-file-preview-title" className="font-semibold text-gray-200">{preview.name}</h3><p className="text-xs text-gray-600">{formatBytes(preview.size)} · {preview.encoding === 'base64' ? 'binary preview encoded as base64' : 'UTF-8 preview'}</p></div><button type="button" className="btn-ghost p-2 ml-auto" onClick={() => setPreview(null)}><X className="w-4 h-4" /></button></div>
        <pre className="flex-1 m-4 p-3 rounded bg-black/40 border border-dark-600 font-mono text-xs text-gray-200 overflow-auto whitespace-pre-wrap break-all">{preview.content}</pre>
        <div className="p-4 border-t border-dark-700 flex justify-end"><button type="button" className="btn-secondary" onClick={() => setPreview(null)}>Close</button></div>
      </div>
    </div>}
  </section>;
}
